import React, { useState, useEffect } from 'react';
import { db } from '../../../utils/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Loader, Save } from 'lucide-react';
import { PageContent } from '../../../types';
import ImageUploader from '../common/ImageUploader';

const PAGES = [
  { id: 'shop', title: 'Available (All Open Items)' },
  { id: 'fragments', title: 'Fragments (The Record)' },
  { id: 'shop-soon', title: 'Surfacing Soon' },
  { id: 'uniform', title: 'Standard Issue' },
  { id: 'artifacts', title: 'Artifacts' },
  { id: 'vault', title: 'The Vault' },
  { id: 'archive', title: 'Archive' },
  { id: 'contact', title: 'Contact Us' },
  { id: 'returns', title: 'Returns & Exchanges' },
  { id: 'faq', title: 'FAQ' },
  { id: 'privacy', title: 'Privacy Policy' },
  { id: 'terms', title: 'Terms of Use' }
];

const PageContentManager: React.FC = () => {
  const [selectedPageId, setSelectedPageId] = useState(PAGES[0].id);
  const [content, setContent] = useState('');
  const [heroImage, setHeroImage] = useState('');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    fetchContent(selectedPageId);
  }, [selectedPageId]);

  // --- Page Content Functions ---
  const fetchContent = async (pageId: string) => {
    setLoading(true);
    setMessage(null);
    try {
      const docRef = doc(db, 'page_content', pageId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as PageContent;
        setContent(data.content || '');
        setHeroImage(data.heroImage || '');
        setTitle(data.title || '');
        setSubtitle(data.subtitle || '');
      } else {
        setContent('');
        setHeroImage('');
        setTitle('');
        setSubtitle('');
      }
    } catch (error) {
      console.error("Error fetching page content:", error);
      setMessage({ type: 'error', text: 'Failed to load content.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      // Save exactly what the Title field holds. Empty means "use the
      // page's built-in title" — never silently stamp the admin menu label
      // onto the customer-facing page.
      await setDoc(doc(db, 'page_content', selectedPageId), {
        title: title.trim(),
        content: content,
        heroImage: heroImage,
        subtitle: subtitle,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      setMessage({ type: 'success', text: 'Content saved successfully!' });
    } catch (error) {
      console.error("Error saving content:", error);
      setMessage({ type: 'error', text: 'Failed to save content.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
      {/* Sidebar Selector */}
      <div className="lg:col-span-1 space-y-2">
        <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-4">Select Page</h3>
        {PAGES.map((page) => (
          <button
            key={page.id}
            onClick={() => setSelectedPageId(page.id)}
            className={`w-full text-left px-4 py-3 text-sm font-heading font-bold uppercase tracking-wider transition-colors border-l-4 ${
              selectedPageId === page.id
                ? 'bg-gray-100 border-black text-black'
                : 'border-transparent text-gray-400 hover:text-black hover:bg-gray-50'
            }`}
          >
            {page.title}
          </button>
        ))}
      </div>

      {/* Editor Area */}
      <div className="lg:col-span-3">
        <div className="bg-white border border-gray-200 p-6 min-h-[600px] flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-heading font-bold text-2xl">
              {PAGES.find(p => p.id === selectedPageId)?.title}
            </h2>
            {message && (
              <span className={`text-xs font-bold px-3 py-1 rounded ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {message.text}
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader className="animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">
                    Hero Image
                  </label>
                  <ImageUploader
                    value={heroImage}
                    onChange={setHeroImage}
                    pathPrefix={`page-assets/${selectedPageId}`}
                    aspectClass="aspect-video"
                    onError={(text) => setMessage({ type: 'error', text })}
                    onUploaded={() => setMessage({ type: 'success', text: 'Image uploaded successfully!' })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">
                    Page Title (optional)
                  </label>
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 text-sm focus:outline-none focus:border-black"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Leave empty to use the page's built-in title"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 text-sm focus:outline-none focus:border-black"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    placeholder="Page subtitle..."
                  />
                </div>
              </div>

              <div className="flex-1 mb-6">
                <textarea
                  className="w-full h-full min-h-[400px] p-4 border border-gray-300 font-mono text-sm resize-none focus:outline-none focus:border-black transition-colors"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Enter page content here (HTML supported)..."
                />
                <div className="mt-2 space-y-1">
                  <p className="text-[10px] text-gray-400 text-right">Supports valid HTML structure.</p>
                  <p className="text-[10px] text-amber-600 flex items-start gap-1">
                    <span>⚠️</span>
                    <span>
                      <strong>Security:</strong> HTML content is automatically sanitized to prevent malicious scripts.
                      JavaScript, event handlers, and unsafe tags will be removed on display.
                      Safe HTML (headings, paragraphs, lists, links, images) is preserved.
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-black text-white px-8 py-3 font-heading font-bold uppercase tracking-widest text-xs hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? <Loader className="animate-spin" size={16} /> : <Save size={16} />}
                  Save Changes
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageContentManager;
